/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, createRef } from 'react'
import { Info as InfoIcon } from 'react-feather'
import { isEmpty } from 'lodash'
import { useForm, Controller } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import {
  Chip,
  Card,
  Box,
  Typography,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
  Button,
  Tab,
  Tabs,
  Tooltip,
  IconButton,
} from '@material-ui/core'
import qs from 'query-string'

import { GapAnalysisHeader, LoadingFeedback } from 'components'
import {
  DialogConfirmChangeAnswer,
  DialogNotApplicableQuestion,
  DialogQuestionInformation,
  TechnologicalControlSection,
} from './components'

import useSnackbar from 'hooks/useSnackbar'
import usePermissions from 'hooks/usePermissions'

import { routes } from 'Routes'
import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'
import useStyles from './styles'

const QuestionsReply = ({
  questionnaire,
  isLoading,
  refresh = () => {},
  companyKind,
  questions,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const snackbar = useSnackbar()
  const location = useLocation()
  const permissions = usePermissions()

  const search = qs.parse(location.search)
  const detachedId = search?.hash
  const questionnaireId = questionnaire?.id
  const isQuestionnaireDisabled =
    questionnaire?.name === constants.questionnaire.FUNDAMENTAL_REQUIREMENTS &&
    !permissions.isSupplier()
  const isNotApplicableDisabled =
    questionnaire?.name === constants.questionnaire.FUNDAMENTAL_REQUIREMENTS &&
    permissions.isSupplier()

  const [loading, setLoading] = useState(false)
  const [currentTab, setCurrentTab] = useState()
  const [openDialogNotApplicable, setOpenDialogNotApplicable] = useState(false)
  const [openDialogConfirmChangeAnswer, setOpenDialogConfirmChangeAnswer] =
    useState(false)
  const [openDialogQuestionInformation, setOpenDialogQuestionInformation] =
    useState(false)

  const [changeReply, setChangeReply] = useState()
  const [questionnaireData, setQuestionnaireData] = useState({ answers: [] })
  const [notApplicableQuestions, setNotApplicableQuestions] = useState([])
  const [questionSelected, setQuestionSelected] = useState()

  const { handleSubmit, control, setValue } = useForm()

  const displayedQuestionByTab = {
    [constants.questions.ALL_TAB]: questions?.applicableChoice,
    [constants.questions.PENDING_TAB]: questions?.pending,
    [constants.questions.ANSWERED_TAB]: questions?.answered,
  }
  const displayQuestions = displayedQuestionByTab[currentTab]

  const refs = questions?.applicableChoice?.reduce((acc, value) => {
    acc[value.id] = createRef()
    return acc
  }, {})

  const submitAnswers = async (questionnaire) => {
    try {
      setLoading(true)

      await service.dponet.questionnaires.reply({
        questionnaireId,
        questionnaire,
      })
      refresh()

      snackbar.open({
        message: 'Questionário respondido com sucesso',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
      setOpenDialogNotApplicable(false)
    }
  }
  const mountQuestionnaireAnswers = (data) => {
    const answers = Object.entries(data).map(([id, answer]) => ({ id, answer }))
    const notApplicableAnswers = answers.filter(
      (question) => question.answer === 'Não se aplica',
    )

    return { answers, notApplicableAnswers }
  }

  const onSubmit = async (data) => {
    const { answers, notApplicableAnswers } = mountQuestionnaireAnswers(data)
    const questionnaireAnswers = { answers: answers }

    const questions = questionnaire.questions.map((question, index) => ({
      index: index + 1,
      ...question,
    }))

    const notApplicableQuestions = questions.filter((question) =>
      notApplicableAnswers
        .map((notApplicableQuestion) => notApplicableQuestion.id)
        .includes(question.id.toString()),
    )

    if (notApplicableAnswers.length > 0) {
      setQuestionnaireData(questionnaireAnswers)
      setNotApplicableQuestions(notApplicableQuestions)
      return setOpenDialogNotApplicable(true)
    }

    await submitAnswers(questionnaireAnswers)
  }

  const getCardStyle = (question) => {
    if (detachedId && parseInt(detachedId) === question.id) {
      return classes.detachedBox
    }

    return ''
  }

  const handleChangeTabs = (_, value) => {
    if (constants.questions.TAB_VALUES.includes(value)) {
      setCurrentTab(value)
    }
  }

  const getBadgeValue = (tagValue) => {
    return displayedQuestionByTab[tagValue]?.length || 0
  }

  const viewDialogConfirmChangeAnswer = (question, target) =>
    question.nonComplianceReport && target.value !== question.openWith

  const dynamicSelectTab = () => {
    if (currentTab || isEmpty(questions?.active)) return

    if (detachedId) {
      const questionInPending = questions?.answered.find(
        (question) => question.id === parseInt(detachedId),
      )

      const tab = questionInPending
        ? constants.questions.ANSWERED_TAB
        : constants.questions.ALL_TAB

      return setCurrentTab(tab)
    }

    const tab = !isEmpty(questions?.pending)
      ? constants.questions.PENDING_TAB
      : constants.questions.ALL_TAB

    setCurrentTab(tab)
  }

  const handleOpenInfoDialog = async (question) => {
    setOpenDialogQuestionInformation(true)
    setQuestionSelected(question)
  }

  useEffect(() => {
    if (isLoading || !detachedId || isEmpty(refs)) {
      return
    }

    if (!refs[detachedId]) {
      return history.replace()
    }

    if (!!refs[detachedId].current) {
      refs[detachedId].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [detachedId, questionnaire, isLoading])

  useEffect(() => {
    dynamicSelectTab()
  }, [questionnaire])

  return (
    <>
      <LoadingFeedback open={loading} />
      <GapAnalysisHeader title='As perguntas desta aba "Minhas Questões" são as aplicáveis para a sua organização. Inicialmente, o DPOnet recomenda certas perguntas para o tamanho e ramo de atuação da sua empresa, mas você pode adaptar os questionários e indicar que alguma pergunta "Não se aplica" ou então buscar novas perguntas em "Outras Questões"' />

      {questionnaire.name === constants.questionnaire.TECHNOLOGICAL_CONTROLS &&
        companyKind !== constants.company.SUPPLIER_KIND && (
          <TechnologicalControlSection
            questionnaire={questionnaire}
            setLoading={setLoading}
            refresh={refresh}
          />
        )}

      {!!currentTab && (
        <Tabs
          scrollButtons="auto"
          onChange={handleChangeTabs}
          textColor="primary"
          indicatorColor="primary"
          value={currentTab}
          variant="standard"
        >
          {constants.questions.TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={
                <Box>
                  {tab.label} ({getBadgeValue(tab.value)})
                </Box>
              }
            />
          ))}
        </Tabs>
      )}
      <Card>
        <Box pb={3}>
          {!isEmpty(displayQuestions) ? (
            <form onSubmit={handleSubmit(onSubmit)}>
              {displayQuestions.map((question, index) => (
                <Box key={question.id}>
                  <Box
                    mx={3}
                    my={2}
                    id={question.id}
                    ref={refs[question.id]}
                    className={getCardStyle(question)}
                  >
                    <Grid container>
                      <Grid item md={9} sm={12}>
                        <Grid container>
                          <Grid item xs={12}>
                            <Typography variant="h5">
                              {index + 1}. {question.theme}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Box width="100%" mt={1} ml={2}>
                              <Typography variant="body1">
                                {question.question}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item sm={6} xs={12}>
                            <Box width="100%" mt={1} ml={2}>
                              <Controller
                                control={control}
                                name={question.id.toString()}
                                defaultValue={question.answer}
                                as={
                                  <Tooltip
                                    title={
                                      isQuestionnaireDisabled
                                        ? 'Sua empresa já adota essa medida porque tem o DPOnet.'
                                        : ''
                                    }
                                  >
                                    <RadioGroup row>
                                      <Box
                                        id={
                                          constants.questionnaire
                                            .QUESTIONNAIRE_REPLY_DRIVER_STEP_3
                                        }
                                      >
                                        <FormControlLabel
                                          value="Sim"
                                          control={<Radio color="primary" />}
                                          label="Sim"
                                          disabled={
                                            isQuestionnaireDisabled ||
                                            permissions.isDpo()
                                          }
                                        />
                                        <FormControlLabel
                                          value="Parcial"
                                          control={<Radio color="primary" />}
                                          label="Parcial"
                                          disabled={isQuestionnaireDisabled}
                                        />
                                        <FormControlLabel
                                          value="Não"
                                          control={<Radio color="primary" />}
                                          label="Não"
                                          disabled={
                                            isQuestionnaireDisabled ||
                                            permissions.isDpo()
                                          }
                                          className={clsx({
                                            [classes.endFormControl]:
                                              isNotApplicableDisabled,
                                          })}
                                        />
                                        {!isNotApplicableDisabled && (
                                          <FormControlLabel
                                            value="Não se aplica"
                                            control={<Radio color="primary" />}
                                            label="Não se aplica"
                                            disabled={
                                              isQuestionnaireDisabled ||
                                              permissions.isDpo()
                                            }
                                            className={classes.endFormControl}
                                          />
                                        )}
                                      </Box>
                                    </RadioGroup>
                                  </Tooltip>
                                }
                                onChange={([event]) => {
                                  if (
                                    viewDialogConfirmChangeAnswer(
                                      question,
                                      event.target,
                                    )
                                  ) {
                                    setChangeReply(event.target)
                                    setOpenDialogConfirmChangeAnswer(true)
                                    event.stopPropagation()
                                    event.target.value = question.answer
                                  }
                                  return event
                                }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item md={3} sm={12}>
                        <Box className={classes.boxStatus}>
                          <Box>
                            <Chip
                              size="small"
                              className={helpers.questions.chipStatusColor(
                                question.status,
                                classes,
                              )}
                              label={helpers.questions.statusName(
                                question.status,
                              )}
                            />
                            {question?.explanation && question?.purpose && (
                              <IconButton
                                onClick={() => handleOpenInfoDialog(question)}
                              >
                                <InfoIcon size={24} />
                              </IconButton>
                            )}
                          </Box>
                          {!!question.answer && !!question.user && (
                            <Box mt={2}>
                              <Typography variant="body2">
                                Respondido por: {question?.user?.name}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  <Divider />
                </Box>
              ))}
              <Box ml={2} mt={2} display="flex" justifyContent="flex-start">
                <Box pr={1}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => {
                      history.push(routes.questionnaire.all)
                    }}
                  >
                    Voltar
                  </Button>
                </Box>
                {!isQuestionnaireDisabled && !permissions.isDpo() && (
                  <Box>
                    <Button color="primary" type="submit" variant="contained">
                      Salvar
                    </Button>
                  </Box>
                )}
              </Box>
            </form>
          ) : (
            <Box mx={4} mt={3}>
              <Typography variant="body1">
                Não existe nenhuma questão com este status.
              </Typography>
            </Box>
          )}
        </Box>
      </Card>

      <DialogNotApplicableQuestion
        open={openDialogNotApplicable}
        setOpen={setOpenDialogNotApplicable}
        handleConfirmation={() => submitAnswers(questionnaireData)}
        questionnaire={notApplicableQuestions}
      />
      <DialogConfirmChangeAnswer
        open={openDialogConfirmChangeAnswer}
        setOpen={setOpenDialogConfirmChangeAnswer}
        changeReply={changeReply}
        setValue={setValue}
      />
      <DialogQuestionInformation
        open={openDialogQuestionInformation}
        setOpen={setOpenDialogQuestionInformation}
        setLoading={setLoading}
        refresh={refresh}
        question={questionSelected}
        questionnaireName={questionnaire?.name}
        questionnaireId={questionnaireId}
      />
    </>
  )
}

export default QuestionsReply
